/**
 * THE TABLE. Every number on the agent surface comes from here or from the
 * chart beside it — never from the assistant's prose.
 *
 * The envelope is SELF-DESCRIBING (`columns[]` carries key, label, role, grain
 * and format), which is what lets this component decide alignment and number
 * format without inferring anything from key names — the inference that ends
 * with a currency column drawn as a count.
 *
 * A wide result scrolls INSIDE its own container. The page body never scrolls
 * sideways.
 */
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';

import type { ColumnFormat, ColumnSpec, Row } from '../protocol';
import { describeScalar, EMPTY_RESULT_TEXT, formatCell, rawTableHeading, rawTableKeys, sortRows } from '../answer-view';
import { nextSort, type SortState } from '../../../components/table/sort';
import { ValueCell } from './Cells';

const NUMERIC_ROLES = new Set(['measure', 'derived']);

export function ResultTable({
  columns,
  rows,
  caption,
  emptyMessage,
}: {
  columns: ColumnSpec[];
  rows: Row[];
  caption?: string;
  /** What an empty table MEANS here — `emptyResultMessage` decides; a table
   *  cannot tell "withheld in full" from "matched nothing" by itself. */
  emptyMessage?: string;
}) {
  // Sorted in the BROWSER, over the whole result (answer-view.ts `sortRows`
  // says why that is correct here). Default: the engine's own order.
  const [sort, setSort] = useState<SortState>({ field: null, order: 'desc' });
  const shown = useMemo(() => sortRows(rows, sort.field, sort.order), [rows, sort]);
  if (rows.length === 0) {
    return (
      <p role="note" className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
        {emptyMessage ?? EMPTY_RESULT_TEXT}
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead className="bg-muted/50">
          <tr>
            {columns.map((column) => {
              const active = sort.field === column.key;
              const Icon = !active ? ChevronsUpDown : sort.order === 'asc' ? ChevronUp : ChevronDown;
              const next = !active ? 'descending' : sort.order === 'desc' ? 'ascending' : 'unsorted';
              const numeric = NUMERIC_ROLES.has(column.role);
              return (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={active ? (sort.order === 'asc' ? 'ascending' : 'descending') : 'none'}
                  className={`whitespace-nowrap px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground ${
                    numeric ? 'text-right' : 'text-left'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setSort((current) => nextSort(current, column.key))}
                    aria-label={`Sort by ${column.label}: ${next}`}
                    className={`group inline-flex items-center gap-1 uppercase ${numeric ? 'flex-row-reverse' : ''} ${
                      active ? 'text-foreground' : 'hover:text-foreground'
                    }`}
                  >
                    <span>
                      {column.label}
                      {column.grain && (
                        <span className="ml-1 font-normal normal-case opacity-70">({column.grain})</span>
                      )}
                    </span>
                    <Icon
                      aria-hidden="true"
                      className={`h-3 w-3 shrink-0 ${
                        active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                      }`}
                    />
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {shown.map((row, index) => (
            <tr key={index} className="border-t">
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`whitespace-nowrap px-3 py-2 ${
                    NUMERIC_ROLES.has(column.role) ? 'text-right' : 'text-left'
                  }`}
                >
                  <ValueCell cell={formatCell(row[column.key], column.format)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The untyped twin, for the two shapes that arrive without a `columns[]`: the
 * compare tool's delta block, and the `records` presentations (source health,
 * catalogue rows).
 *
 * It goes through `describeScalar`, which routes a `null` to the SAME em-dash
 * component the typed path uses — so the fabricated-zero rule holds on the
 * shapes that have no column spec to state a format.
 */
export function RawTable({
  rows,
  caption,
  columns,
}: {
  rows: Record<string, unknown>[];
  caption?: string;
  /**
   * The column specs, WHEN THE CALLER HAS THEM. Without this, every number is
   * rendered exactly but unitlessly — so a revenue delta of 249900 cents
   * printed `249,900`, which an operator reads as dollars. That is a 100x error
   * on the block whose entire purpose is the comparison, and it rendered on the
   * SHARE page too, which is what one admin sends to another.
   *
   * ⚠️ THIS COMMENT WAS FALSE WHEN FIRST WRITTEN. It claimed "this makes the
   * delta block correct" while NO call site passed the prop — the whole thing
   * was dead code documented as live, which is worse than absent because it
   * retires the finding without closing it. AnswerCard now passes
   * `presentation.envelopes[0]?.envelope.columns`, so the claim is finally true.
   *
   * ⚠️ AND THE PARAGRAPH THAT USED TO FOLLOW IT WAS ALSO WRONG. It read:
   * "share.tsx and PinnedAnswersTile.tsx render a STORED body that carries no
   * specs. Those keep the exact-but-unitless rendering, which is honest."
   *
   * It is not honest. Exact-but-unitless is honest only when the unit is
   * VISIBLE, and on those two surfaces it appears nowhere — so that paragraph
   * accepted precisely the misread the paragraph above it documents. Four lines
   * apart, the same docstring called `249,900` a 100x error and called it
   * honest.
   *
   * "Carries no specs" was true of the payload and false of the system: the
   * route that serves that body holds the metric definition already, to read
   * `metric_id` and `deep_link`. It now serves `columns` too, so all three call
   * sites pass them and the share page renders the same money as the chat.
   * `admin-agent-library.test.ts` asserts the served format AND the rendered
   * string, so a prop restored without the data behind it still fails.
   */
  columns?: readonly { key: string; format: ColumnFormat; label?: string }[];
}) {
  const formatFor = (key: string): ColumnFormat | undefined =>
    columns?.find((c) => c.key === key)?.format;
  // The metric's OWN label when the route served one; the key, spaced, only as
  // the fallback (an older stored body, or a date column, which has no label).
  const headingFor = (key: string): string => rawTableHeading(key, columns);
  const keys = rawTableKeys(rows, columns);
  if (rows.length === 0 || keys.length === 0) {
    return <p className="text-sm text-muted-foreground">No rows.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead className="bg-muted/50">
          <tr>
            {keys.map((key) => (
              <th
                key={key}
                scope="col"
                className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground"
              >
                {headingFor(key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-t">
              {keys.map((key) => (
                <td key={key} className="whitespace-nowrap px-3 py-2">
                  <ValueCell cell={describeScalar(row[key], formatFor(key))} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
